import cruces from "./datos/cruces.json"
import sistema2 from "./datos/sistema2.json"
import sistema3 from "./datos/sistema3.json"
import type { DatosMapa, FlujoMapa, NodoMapa } from "./grafo3d"

/**
 * Los datos del mapa del sistema (24/10): un JSON por sistema y los flujos que cruzan entre
 * ellos (`datos/`). Son DATOS del repositorio, no un dibujo: salen con cada despliegue del
 * 3.0, y `mapa.test.ts` falla si un módulo del menú o una tarea del beat no están en el
 * mapa del 3.0. El del 2.0 lo vigila `services/mapa_vigencia.py` contra los archivos del
 * hosting.
 *
 * ⚠ **Cada sistema tiene un nodo que representa al OTRO** (`s2` en el 3.0, `s3` en el 2.0):
 * en su mapa propio resume la integración. En la vista unida esos dos nodos se quitan y
 * los flujos reales entre módulos de uno y otro salen de `cruces.json`.
 */

export type ArchivoMapa = {
  sistema: string
  actualizado: string
  areas: Record<string, { nombre: string; color: string }>
  nodos: (NodoMapa & { modulos?: string[]; tareas?: string[] })[]
  flujos: FlujoMapa[]
  /** Sólo en el 2.0: los archivos del hosting cuando se dibujó, para avisar si cambian. */
  archivos?: string[]
  /** Cuándo se dibujó (ISO, UTC): lo modificado allá después avisa. */
  dibujado?: string
}

export const MAPA_3 = sistema3 as ArchivoMapa
export const MAPA_2 = sistema2 as ArchivoMapa
export const CRUCES = (cruces as { cruces: FlujoMapa[] }).cruces

/** El nodo que, en cada mapa, representa al otro sistema. */
const REPRESENTA_AL_OTRO = { "3.0": "s2", "2.0": "s3" } as const

export const SISTEMAS = {
  "3.0": { nombre: "Sistema 3.0", color: "#e2bd6b" },
  "2.0": { nombre: "Sistema 2.0", color: "#5ec8e5" },
} as const

/** Los dos sistemas en un mapa: cada uno su galaxia, unidos por sus flujos reales. */
export function unir(): DatosMapa {
  const nodos: NodoMapa[] = []
  const flujos: FlujoMapa[] = []
  for (const [sis, mapa, prefijo] of [
    ["3.0", MAPA_3, "3:"],
    ["2.0", MAPA_2, "2:"],
  ] as const) {
    const otro = REPRESENTA_AL_OTRO[sis]
    for (const n of mapa.nodos) {
      if (n.id === otro) continue
      nodos.push({
        id: prefijo + n.id,
        nombre: n.nombre,
        area: sis,
        grupo: n.area,
        sistema: sis,
        color: mapa.areas[n.area]?.color,
        desc: `${SISTEMAS[sis].nombre} · ${mapa.areas[n.area]?.nombre ?? ""}. ${n.desc}`,
      })
    }
    for (const f of mapa.flujos) {
      if (f.de === otro || f.a === otro) continue
      flujos.push({ de: prefijo + f.de, a: prefijo + f.a, txt: f.txt })
    }
  }
  return { areas: { ...SISTEMAS }, nodos, flujos: [...flujos, ...CRUCES] }
}

export function propio(mapa: ArchivoMapa): DatosMapa {
  return { areas: mapa.areas, nodos: mapa.nodos, flujos: mapa.flujos }
}
